import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AdvancedMarker, Pin, InfoWindow, useMap, useAdvancedMarkerRef, MapControl, ControlPosition } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { Plus, EyeOff, Eye } from 'lucide-react';

const MemoizedCustomerPin = React.memo(({ c, lat, lng, labelText, color, isSelected, onClick, scale, showLabels, showCustomerNames, shortName, onMarkerMounted, onMarkerUnmounted }) => {
  const [markerRef, marker] = useAdvancedMarkerRef();

  useEffect(() => {
    if (marker) {
      onMarkerMounted(marker, c.cd_no, color);
    }
    return () => {
      if (marker) {
        onMarkerUnmounted(marker, c.cd_no, color);
      }
    };
  }, [marker, c.cd_no, color, onMarkerMounted, onMarkerUnmounted]);

  return (
    <AdvancedMarker 
      ref={markerRef} 
      position={{ lat, lng }}
      title={`${c.search_name} (${labelText})`}
      onClick={() => onClick(c)}
      style={{ zIndex: isSelected ? 100 : 1 }}
    >
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', transform: `scale(${scale})`, transformOrigin: 'bottom center', transition: 'transform 0.2s ease' }}>
        {showLabels && (
          <div style={{
            position: 'absolute',
            top: '-25px',
            backgroundColor: 'white',
            border: `2px solid ${color}`,
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#1f2937',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: isSelected ? 100 : 1
          }}>
            {labelText}
          </div>
        )}
        {showCustomerNames && (
          <div style={{
            position: 'absolute',
            left: '24px',
            top: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${color}`,
            borderRadius: '4px',
            padding: '2px 6px',
            fontSize: '11px',
            fontWeight: '600',
            color: '#334155',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            zIndex: isSelected ? 100 : 0,
            pointerEvents: 'none'
          }}>
            {shortName}
          </div>
        )}
        <svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))' }}>
          <path d="M12 0C5.37258 0 0 5.37258 0 12C0 21 12 36 12 36C12 36 24 21 24 12C24 5.37258 18.6274 0 12 0Z" fill={color} stroke="#ffffff" strokeWidth="1.5"/>
          <circle cx="12" cy="12" r="4.5" fill="#ffffff"/>
        </svg>
      </div>
    </AdvancedMarker>
  );
});

export default function CustomerPins({ customers, teamColors, onAddToTrip, focusedCustomer, onClearFocus, hiddenTeams = [], hiddenSales = [], hiddenAfterSales = [], salesCodeToTeamName = {}, showLabels = true, showCustomerNames = false }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isIsolated, setIsIsolated] = useState(false);
  const map = useMap();
  const [zoomLevel, setZoomLevel] = useState(6);
  const clusterersRef = useRef({});
  const markersRef = useRef({});
  const renderTimeoutRef = useRef({});

  const showCustomerNamesRef = useRef(showCustomerNames);
  useEffect(() => {
    showCustomerNamesRef.current = showCustomerNames;
    // Rerender all clusters if the toggle changes so names hide/show immediately
    Object.values(clusterersRef.current).forEach(clusterer => {
      if (clusterer) clusterer.render();
    });
  }, [showCustomerNames]);

  const scheduleRender = useCallback((color) => {
    if (renderTimeoutRef.current[color]) {
      clearTimeout(renderTimeoutRef.current[color]);
    }
    renderTimeoutRef.current[color] = setTimeout(() => {
      const clusterer = clusterersRef.current[color];
      if (clusterer) {
        clusterer.render();
      }
    }, 50);
  }, []);

  const getClusterer = useCallback((color) => {
    if (!map) return null;
    if (!clusterersRef.current[color]) {
      const renderer = {
        render: ({ count, position, markers }) => {
          const div = document.createElement('div');
          div.style.position = 'relative';

          const circle = document.createElement('div');
          circle.style.backgroundColor = color;
          circle.style.color = 'white';
          circle.style.borderRadius = '50%';
          circle.style.width = '36px';
          circle.style.height = '36px';
          circle.style.display = 'flex';
          circle.style.alignItems = 'center';
          circle.style.justifyContent = 'center';
          circle.style.fontWeight = 'bold';
          circle.style.fontSize = '12px';
          circle.style.border = '2px solid white';
          circle.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          circle.innerText = String(count);

          div.appendChild(circle);

          if (showCustomerNamesRef.current && markers && markers.length > 0) {
            const currentZoom = map.getZoom();
            if (currentZoom >= 13) {
              const namesDiv = document.createElement('div');
              namesDiv.style.position = 'absolute';
              namesDiv.style.left = '42px';
              namesDiv.style.top = '50%';
              namesDiv.style.transform = 'translateY(-50%)';
              namesDiv.style.display = 'flex';
              namesDiv.style.flexDirection = 'column';
              namesDiv.style.gap = '2px';
              namesDiv.style.pointerEvents = 'none';
              namesDiv.style.zIndex = '100';

              markers.slice(0, 5).forEach(m => {
                let text = m.title || '';
                if (text.includes(' (')) {
                  text = text.split(' (')[0];
                }
                const shortName = text.length > 10 ? text.substring(0, 10) + '...' : text;
                const nameItem = document.createElement('div');
                nameItem.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                nameItem.style.border = `1px solid ${color}`;
                nameItem.style.borderRadius = '4px';
                nameItem.style.padding = '2px 6px';
                nameItem.style.fontSize = '11px';
                nameItem.style.fontWeight = '600';
                nameItem.style.color = '#334155';
                nameItem.style.whiteSpace = 'nowrap';
                nameItem.style.boxShadow = '0 1px 3px rgba(0,0,0,0.15)';
                nameItem.innerText = shortName;
                namesDiv.appendChild(nameItem);
              });
              
              if (markers.length > 5) {
                const moreItem = document.createElement('div');
                moreItem.style.fontSize = '10px';
                moreItem.style.color = '#64748b';
                moreItem.style.padding = '0 4px';
                moreItem.style.fontWeight = 'bold';
                moreItem.innerText = `+${markers.length - 5} more`;
                namesDiv.appendChild(moreItem);
              }
              
              div.appendChild(namesDiv);
            }
          }

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: div,
          });
        }
      };

      clusterersRef.current[color] = new MarkerClusterer({
        map,
        renderer,
      });
    }
    return clusterersRef.current[color];
  }, [map]);

  const onMarkerMounted = useCallback((marker, id, color) => {
    if (!markersRef.current[color]) markersRef.current[color] = {};
    if (!markersRef.current[color][id]) {
      markersRef.current[color][id] = marker;
      const clusterer = getClusterer(color);
      if (clusterer) {
        clusterer.addMarker(marker, true);
        scheduleRender(color);
      }
    }
  }, [getClusterer, scheduleRender]);

  const onMarkerUnmounted = useCallback((marker, id, color) => {
    if (markersRef.current[color] && markersRef.current[color][id]) {
      delete markersRef.current[color][id];
      const clusterer = clusterersRef.current[color];
      if (clusterer) {
        clusterer.removeMarker(marker, true);
        scheduleRender(color);
      }
    }
  }, [scheduleRender]);

  useEffect(() => {
    return () => {
      Object.values(clusterersRef.current).forEach(c => {
        if (c) c.clearMarkers();
      });
      Object.values(renderTimeoutRef.current).forEach(t => {
        if (t) clearTimeout(t);
      });
    };
  }, []);

  const handleSelectCustomer = useCallback((c) => {
    setSelectedCustomer(c);
  }, []);

  useEffect(() => {
    if (!map) return;
    let timeoutId;
    const listener = map.addListener('zoom_changed', () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setZoomLevel(map.getZoom() || 6);
      }, 150);
    });
    setZoomLevel(map.getZoom() || 6);
    return () => {
      google.maps.event.removeListener(listener);
      clearTimeout(timeoutId);
    };
  }, [map]);

  useEffect(() => {
    if (focusedCustomer) {
      setSelectedCustomer(focusedCustomer);
      setIsIsolated(true);
    } else {
      setIsIsolated(false);
    }
  }, [focusedCustomer]);

  let scale = 1;
  if (zoomLevel < 10) {
    scale = Math.max(0.15, 1 - (10 - zoomLevel) * 0.25); 
  }

  const processedCustomers = React.useMemo(() => {
    if (!customers || customers.length === 0) return [];
    
    const validList = [];
    for (let i = 0; i < customers.length; i++) {
      const c = customers[i];
      if (isIsolated && selectedCustomer && c.cd_no !== selectedCustomer.cd_no) continue;
      if (!c.lat || !c.lng) continue;

      const lat = parseFloat(c.lat);
      const lng = parseFloat(c.lng);
      if (isNaN(lat) || isNaN(lng)) continue;

      const teamName = c.team_name || '';

      const teamHidden = hiddenTeams.includes(teamName);

      if (teamHidden) {
        continue;
      }

      let labelText = '';
      if (c.update_sales_code && c.update_after_sales_code && c.update_sales_code !== c.update_after_sales_code) {
          labelText = `${c.update_sales_code}/${c.update_after_sales_code}`;
      } else if (c.update_sales_code) {
          labelText = c.update_sales_code;
      } else if (c.update_after_sales_code) {
          labelText = c.update_after_sales_code;
      }

      const teamForColor = teamName;
      const color = teamForColor && teamColors[teamForColor] ? teamColors[teamForColor] : '#3b82f6';
      
      let shortName = c.search_name || '';
      if (shortName.length > 10) shortName = shortName.substring(0, 10) + '...';

      validList.push({ ...c, parsedLat: lat, parsedLng: lng, labelText, color, shortName });
    }
    
    // Collision detection
    if (showCustomerNames) {
      const collisionThreshold = 100 / Math.pow(2, Math.max(0, zoomLevel));
      const gridSize = collisionThreshold;
      const grid = {};

      // Build grid
      for (let i = 0; i < validList.length; i++) {
        const item = validList[i];
        const gridX = Math.floor(item.parsedLng / gridSize);
        const gridY = Math.floor(item.parsedLat / gridSize);
        const key = `${gridX}_${gridY}`;
        if (!grid[key]) grid[key] = [];
        grid[key].push(i);
      }

      // Check collisions using grid
      for (let i = 0; i < validList.length; i++) {
        let hasSpace = true;
        const item = validList[i];
        const gridX = Math.floor(item.parsedLng / gridSize);
        const gridY = Math.floor(item.parsedLat / gridSize);
        
        outer: for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const key = `${gridX + dx}_${gridY + dy}`;
            const cell = grid[key];
            if (cell) {
              for (let c = 0; c < cell.length; c++) {
                const j = cell[c];
                if (i === j) continue;
                const dLat = Math.abs(item.parsedLat - validList[j].parsedLat);
                const dLng = Math.abs(item.parsedLng - validList[j].parsedLng);
                if (dLat + dLng < collisionThreshold) {
                  hasSpace = false;
                  break outer;
                }
              }
            }
          }
        }
        validList[i].hasSpace = hasSpace;
      }
    }

    return validList;
  }, [customers, hiddenTeams, hiddenSales, hiddenAfterSales, isIsolated, selectedCustomer, salesCodeToTeamName, teamColors, zoomLevel, showCustomerNames]);

  const [renderedCount, setRenderedCount] = useState(150);

  useEffect(() => {
    // Reset the rendered count only when the dataset or name display toggle changes
    setRenderedCount(150);
  }, [processedCustomers.length, showCustomerNames]);

  useEffect(() => {
    if (processedCustomers && renderedCount < processedCustomers.length) {
      const timer = setTimeout(() => {
        setRenderedCount(prev => Math.min(prev + 150, processedCustomers.length));
      }, 35); // Yield to main thread every 150 markers for smooth 60fps
      return () => clearTimeout(timer);
    }
  }, [renderedCount, processedCustomers.length]);

  if (!customers || customers.length === 0) return null;

  const total = processedCustomers.length;
  const isRendering = total > 0 && renderedCount < total;

  return (
    <>
      {isRendering && (
        <MapControl position={ControlPosition.TOP_CENTER}>
          <div style={{
            marginTop: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '10px 20px',
            borderRadius: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#2563eb',
            border: '2px solid #bfdbfe',
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              width: '18px', height: '18px',
              border: '3px solid #bfdbfe',
              borderTop: '3px solid #2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            กำลังวาดหมุดบนแผนที่... ({Math.min(renderedCount, total)} / {total})
            <style>{`
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
          </div>
        </MapControl>
      )}

      {processedCustomers.slice(0, renderedCount).map((c, idx) => {
        return (
          <MemoizedCustomerPin
            key={c.cd_no || idx}
            c={c}
            lat={c.parsedLat}
            lng={c.parsedLng}
            labelText={c.labelText}
            color={c.color}
            isSelected={selectedCustomer?.cd_no === c.cd_no}
            onClick={handleSelectCustomer}
            scale={scale}
            showLabels={showLabels && c.labelText !== ''}
            showCustomerNames={showCustomerNames && c.hasSpace !== false}
            shortName={c.shortName}
            onMarkerMounted={onMarkerMounted}
            onMarkerUnmounted={onMarkerUnmounted}
          />
        );
      })}

      {selectedCustomer && (
        <InfoWindow
          position={{ lat: parseFloat(selectedCustomer.lat), lng: parseFloat(selectedCustomer.lng) }}
          onCloseClick={() => {
            setSelectedCustomer(null);
            setIsIsolated(false);
            if (onClearFocus) onClearFocus();
          }}
          pixelOffset={[0, -30]}
        >
          <div style={{ padding: '0.5rem', maxWidth: '250px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-primary)' }}>{selectedCustomer.search_name}</h4>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', lineHeight: '1.4', marginBottom: '0.75rem' }}>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Group:</strong> {selectedCustomer.customer_group}</p>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>CD No:</strong> {selectedCustomer.cd_no}</p>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Customer Name:</strong> {selectedCustomer.customer_name || '-'}</p>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Sales Code:</strong> {selectedCustomer.update_sales_code || '-'}</p>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>After Sales:</strong> {selectedCustomer.update_after_sales_code || '-'}</p>
              <p style={{ margin: '0 0 0.25rem 0' }}><strong>Team:</strong> {selectedCustomer.team_name || 'ยังไม่ผูกเซลล์'}</p>
              <p style={{ margin: '0' }}><strong>Address:</strong> {selectedCustomer.address} {selectedCustomer.city}</p>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {onAddToTrip && (
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1, padding: '0.4rem', fontSize: '0.85rem', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#3b82f6', color: 'white' }}
                  onClick={() => onAddToTrip(selectedCustomer)}
                >
                  <Plus size={16} style={{ marginRight: '0.3rem' }} />
                  เพิ่มลงในทริป
                </button>
              )}
              <button 
                className="btn btn-secondary" 
                style={{ flex: 1, padding: '0.4rem', fontSize: '0.85rem', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: isIsolated ? '#f3f4f6' : '#ef4444', color: isIsolated ? '#1f2937' : 'white', border: isIsolated ? '1px solid #d1d5db' : 'none' }}
                onClick={() => setIsIsolated(!isIsolated)}
              >
                {isIsolated ? (
                  <><Eye size={16} style={{ marginRight: '0.3rem' }} /> เลิกซ่อนหมุดอื่นๆ</>
                ) : (
                  <><EyeOff size={16} style={{ marginRight: '0.3rem' }} /> ซ่อนหมุดอื่นๆ</>
                )}
              </button>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
}
